import React from 'react'
import { Box, Paper, Typography, Button, Fade, CircularProgress, TextField } from '@mui/material'
import { CameraAlt, ImageOutlined } from '@mui/icons-material'

type Props = {
  profilePicture: string | null
  professionalBio: string
  isUploadingProfilePhoto: boolean
  onProfilePictureChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onRemoveProfilePicture: () => void
  onBioChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const AboutYouStep: React.FC<Props> = ({
  profilePicture,
  professionalBio,
  isUploadingProfilePhoto,
  onProfilePictureChange,
  onRemoveProfilePicture,
  onBioChange,
}) => {
  const [isDragging, setIsDragging] = React.useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file && onProfilePictureChange) {
      const fakeEvent = {
        target: {
          files: [file],
          value: '',
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>
      onProfilePictureChange(fakeEvent)
    }
  }

  return (
    <Box>
      {/* Profile Picture Section */}
      <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'grey.800',
              mb: 2,
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            Add Agency logo
          </Typography>

          <Box
            component="label"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              p: 2.5,
              borderRadius: 3,
              border: '1.5px dashed',
              borderColor: isDragging ? '#7A6FA1' : '#D5D1E1',
              bgcolor: isDragging ? '#F5F3FA' : '#FAFAFD',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
              '&:hover': {
                borderColor: '#7A6FA1',
                bgcolor: '#F5F3FA',
              },
            }}
          >
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={onProfilePictureChange}
              disabled={isUploadingProfilePhoto}
            />

            {/* Avatar Circle Container */}
            <Box
              sx={{
                width: 96,
                height: 96,
                borderRadius: '50%',
                bgcolor: '#E3E1EC',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                flexShrink: 0,
                overflow: 'visible',
              }}
            >
              {profilePicture ? (
                <Box
                  component="img"
                  src={profilePicture}
                  alt="Agency Logo"
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <ImageOutlined sx={{ fontSize: 36, color: '#8F87A7' }} />
              )}

              {/* Camera Badge overlay on bottom right */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.12)',
                  border: '1.5px solid #F0EDF5',
                }}
              >
                <CameraAlt sx={{ fontSize: 16, color: '#7A6FA1' }} />
              </Box>

              {/* Uploading loading spinner overlay */}
              {isUploadingProfilePhoto && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={24} sx={{ color: '#7A6FA1' }} />
                </Box>
              )}
            </Box>

            {/* Text details container */}
            <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Typography
                variant="body1"
                sx={{
                  color: '#4E4861',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  lineHeight: 1.4,
                }}
              >
                Drop your photo here or{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#7A6FA1',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Select a file
                </Box>
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#8F87A7',
                  mt: 0.5,
                  fontSize: '0.8rem',
                }}
              >
                Supports: JPG, PNG
              </Typography>

              {profilePicture && (
                <Typography
                  variant="caption"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemoveProfilePicture();
                  }}
                  sx={{
                    color: '#D32F2F',
                    cursor: 'pointer',
                    fontWeight: 600,
                    mt: 1,
                    width: 'fit-content',
                    fontSize: '0.8rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Remove logo
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>

      {/* Professional Bio Section */}
      <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: 'grey.700',
              mb: 2,
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            Description
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={professionalBio}
            onChange={onBioChange}
            placeholder="Tell us about yourself, your experience, and your approach to therapy..."
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '& fieldset': {
                  borderColor: '#7A6FA11A',
                },
                '&:hover fieldset': {
                  borderColor: '#7A6FA11A',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#7A6FA1',
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                lineHeight: 1.6,
              },
            }}
          />
        </Box>
      </Fade>
    </Box>
  )
}

export default AboutYouStep

